import type { GameEvent, GameEventType } from '../interfaces/index.js'

type Handler = (event: GameEvent) => void

export class EventBus {
  private listeners: Map<GameEventType, Set<Handler>>
  private history: GameEvent[]
  private maxHistory: number

  constructor(maxHistory: number = 500) {
    this.listeners = new Map()
    this.history = []
    this.maxHistory = maxHistory
  }

  on(type: GameEventType, handler: Handler): () => void {
    let set = this.listeners.get(type)
    if (!set) {
      set = new Set()
      this.listeners.set(type, set)
    }
    set.add(handler)
    return () => {
      this.listeners.get(type)?.delete(handler)
    }
  }

  once(type: GameEventType, handler: Handler): () => void {
    const wrapper: Handler = (event: GameEvent) => {
      unsubscribe()
      handler(event)
    }
    const unsubscribe = this.on(type, wrapper)
    return unsubscribe
  }

  emit(event: GameEvent): void {
    this.history.push(event)
    if (this.history.length > this.maxHistory) {
      this.history.splice(0, this.history.length - this.maxHistory)
    }
    const set = this.listeners.get(event.type)
    if (set) {
      for (const handler of Array.from(set)) {
        try {
          handler(event)
        } catch (_err) {
          // silently swallow handler exceptions
        }
      }
    }
  }

  off(type?: GameEventType): void {
    if (type !== undefined) {
      this.listeners.delete(type)
    } else {
      this.listeners.clear()
    }
  }

  listenerCount(type: GameEventType): number {
    return this.listeners.get(type)?.size ?? 0
  }

  getHistory(limit?: number): GameEvent[] {
    if (limit === undefined) {
      return [...this.history]
    }
    return this.history.slice(-limit)
  }

  clearHistory(): void {
    this.history = []
  }
}

export function createEvent(
  type: GameEventType,
  playerId: string,
  payload: Record<string, unknown>,
  nowMs: number
): GameEvent {
  return { type, playerId, payload, timestamp: nowMs }
}

export const eventBus = new EventBus()
